import { useNavigate, useSearch } from '@tanstack/react-router'
import { INSTALL_FLOW_STEPS, type StepId } from './installFlowConfig'

/**
 * Type-safe navigation hook for the install flow.
 * Uses URL params as source of truth - no store needed.
 *
 * Architecture:
 * - URL contains: step, locationId, sublocationId, installationId
 * - Navigation updates URL directly
 * - TanStack Query provides entity data
 */

/**
 * Optional params that can be passed when navigating.
 * Use this to update IDs and other params in the URL as user progresses.
 */
export interface NavigateParams {
  locationId?: number
  sublocationId?: number
  installationId?: number
  installationName?: string // Used when creating new installation (entered on installation step)
  scanId?: string // Optional nameplate scan ID for auto-filling equipment details
  installSensorKitSerialNumber?: string // Sensor kit serial number (captured at sensor-scan step or from sync page)
}

export interface UseInstallFlowNavigationResult {
  /**
   * Navigate to a specific step by ID.
   * Optionally update URL params (locationId, sublocationId, installationId).
   *
   * @example
   * goToStep('link', { installationId: 123 })
   */
  goToStep: (stepId: StepId, params?: NavigateParams) => void

  /**
   * Navigate to the next step in the flow sequence.
   * Optionally update URL params.
   */
  goToNextStep: (params?: NavigateParams) => void

  /**
   * Navigate to the previous step in the flow sequence.
   */
  goToPreviousStep: () => void
}

/**
 * Custom hook for type-safe install flow navigation.
 * Works with the flat route structure using query parameters.
 *
 * Benefits:
 * - Type-safe step IDs (can't typo)
 * - Direct URL updates (no intermediate store)
 * - Preserves search params (QR code serial, etc.)
 * - Uses centralized step config for sequencing
 */
export function useInstallFlowNavigation(): UseInstallFlowNavigationResult {
  const navigate = useNavigate()
  const searchParams = useSearch({ from: '/_authenticated/organization/$organizationId/install-flow' })
  const currentStep = searchParams.step

  /**
   * Navigate to a specific step, optionally updating URL params.
   */
  const goToStep = (stepId: StepId, params?: NavigateParams) => {
    navigate({
      search: (prev) => ({
        ...prev, // Preserve all existing params (QR code serial, etc.)
        step: stepId,
        // Update IDs if provided, otherwise preserve existing
        ...(params?.locationId !== undefined && { locationId: params.locationId }),
        ...(params?.sublocationId !== undefined && { sublocationId: params.sublocationId }),
        ...(params?.installationId !== undefined && { installationId: params.installationId }),
        ...(params?.installationName !== undefined && { installationName: params.installationName }),
        ...(params?.scanId !== undefined && { scanId: params.scanId }),
        ...(params?.installSensorKitSerialNumber !== undefined && { installSensorKitSerialNumber: params.installSensorKitSerialNumber }),
      }),
    })
  }

  /**
   * Navigate to the next step in the flow sequence.
   */
  const goToNextStep = (params?: NavigateParams) => {
    const currentIndex = INSTALL_FLOW_STEPS.findIndex((step) => step.id === currentStep)

    if (currentIndex === -1) {
      console.error(`Current step not found: ${currentStep}`)
      return
    }

    if (currentIndex >= INSTALL_FLOW_STEPS.length - 1) {
      console.warn('Already at the last step')
      return
    }

    const nextStep = INSTALL_FLOW_STEPS[currentIndex + 1]
    goToStep(nextStep.id, params)
  }

  /**
   * Navigate to the previous step in the flow sequence.
   */
  const goToPreviousStep = () => {
    const currentIndex = INSTALL_FLOW_STEPS.findIndex((step) => step.id === currentStep)

    if (currentIndex === -1) {
      console.error(`Current step not found: ${currentStep}`)
      return
    }

    if (currentIndex <= 0) {
      console.warn('Already at the first step')
      return
    }

    const previousStep = INSTALL_FLOW_STEPS[currentIndex - 1]
    goToStep(previousStep.id)
  }

  return {
    goToStep,
    goToNextStep,
    goToPreviousStep,
  }
}
